import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/images/prosecomlogo-white.svg';
import { LoadStatus, colors } from '../constants/constants';
import { AssessmentSteps, setAssessmentStep } from '../redux/reducers/assessmentReducer';
import { saveUnsaved, signOut } from '../redux/reducers/authenticationReducer';
import { initializeByOrganization as initializeFindings } from '../redux/reducers/findingsReducer';
import { selectOrganizationLoadStatus, selectSelectedOrganizationData } from '../redux/reducers/organizationReducer';

export default function Sidebar() {
  const navigate = useNavigate();
  const style = {
    drawerWidth: 300,
    drawer: { height: '100%', minHeight: '800px' },
    drawerOpenButton: { width: '40px', height: '40px', color: colors.mainGreen },
    sidebarDivider: { borderBottomWidth: 2, mx: '10%', background: colors.mainGreen },
    sidebarNavText: { textAlign: 'center', color: colors.mainGreen },
    buttonIcon: { color: colors.mainGreen },
  };

  const [open, setOpen] = useState(true);
  const organizationLoadStatus = useSelector(selectOrganizationLoadStatus);
  const { organizationId, organizationName } = useSelector(selectSelectedOrganizationData);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const toggleDrawer = () => {
    setOpen(!open);
  };

  const MuiDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
    '& .MuiDrawer-paper': {
      background: '#201547',
      position: 'relative',
      width: style.drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(5),
        },
      }),
    },
  }));

  const navigateToHome = () => {
    navigate('/home');
  };

  const navigateToAssess = () => {
    dispatch(setAssessmentStep(AssessmentSteps.ASSESSMENT_SELECTION));
    navigate('/assess');
  };

  const navigateToActivities = () => {
    dispatch(initializeFindings(organizationId));
    navigate('/activities');
  };

  const navigateToOrganization = () => {
    navigate('/organization');
  };
  const navigateToFrameworks = () => {
    navigate('/frameworks');
  };

  const handleLogout = () => {
    dispatch(signOut());
  };

  const handleSelectOrganization = () => {
    dispatch(saveUnsaved());
    navigate('/selection');
  };

  return (
    <MuiDrawer variant="permanent" open={open} sx={style.drawer}>
      <Box display="flex" justifyContent="flex-end">
        <IconButton id='drawer-toggle' onClick={toggleDrawer} sx={style.drawerOpenButton}>
          {open && <KeyboardDoubleArrowLeftIcon variant="sideBarIcon" />}
          {!open && <KeyboardDoubleArrowRightIcon />}
        </IconButton>
      </Box>
      {open && (
        <>
          <List sx={style.sidebarNavText}>
            <Stack direction="column" spacing={2} mt={0} variant="headerRow">
              <Stack direction="column" spacing={1}>
                <Typography id='current-organization-name' variant="organizationName">{organizationName}</Typography>
                <Typography variant="drawerOrgSub">{t('sidebar.organization')}</Typography>
              </Stack>
              <Button
                size="small"
                variant="contained"
                onClick={handleSelectOrganization}
                id="organization-selection-sidebar"
                startIcon={<BusinessOutlinedIcon />}
              >
                <Typography id='switch-organization-sidebar' variant="drawerButton">{t('sidebar.selectOrganization')}</Typography>
              </Button>
            </Stack>
            <ListItemButton id='home-view-sidebar' onClick={navigateToHome} disabled={organizationLoadStatus !== LoadStatus.SUCCESS}>
              <ListItemText
                sx={style.sidebarNavText}
                primary={<Typography variant="drawer">{t('sidebar.home')}</Typography>}
              />
            </ListItemButton>
            <ListItemButton id='assements-view-sidebar' onClick={navigateToAssess} disabled={organizationLoadStatus !== LoadStatus.SUCCESS}>
              <ListItemText
                sx={style.sidebarNavText}
                primary={<Typography variant="drawer">{t('sidebar.assessments')}</Typography>}
              />
            </ListItemButton>
            <ListItemButton id='activities-view-sidebar' onClick={navigateToActivities} disabled={organizationLoadStatus !== LoadStatus.SUCCESS}>
              <ListItemText
                sx={style.sidebarNavText}
                primary={<Typography variant="drawer">{t('sidebar.activities')}</Typography>}
              />
            </ListItemButton>
            <Box sx={{ mt: '40px', mb: '24px' }}>
              <Divider variant="middle" sx={style.sidebarDivider} />
            </Box>
            <Box sx={{ mb: '20px' }}>
              <Typography variant="drawerOrgHeader">{t('sidebar.organization')}</Typography>
            </Box>
            <ListItemButton id='frameworks-view-sidebar' onClick={navigateToFrameworks} disabled={organizationLoadStatus !== LoadStatus.SUCCESS}>
              <ListItemText
                sx={style.sidebarNavText}
                primary={<Typography variant="drawer">{t('sidebar.frameworks')}</Typography>}
              />
            </ListItemButton>
            <ListItemButton id='organizations-view-sidebar' onClick={navigateToOrganization} disabled={organizationLoadStatus !== LoadStatus.SUCCESS}>
              <ListItemText
                sx={style.sidebarNavText}
                primary={<Typography variant="drawer">{t('sidebar.settings')}</Typography>}
              />
            </ListItemButton>
            <Box sx={{ mt: '24px', mb: '24px' }}>
              <Divider variant="middle" sx={style.sidebarDivider} />
            </Box>
            {/* Admin section hidden until workspace implementation finishes. */}
            <Stack direction="column" spacing={1.5} mt={5} mb={5} variant="headerRow" sx={{ display: 'none' }}>
              <Box mb="12px">
                <Typography variant="drawerAdmin">{t('sidebar.admin')}</Typography>
              </Box>
              <ListItemButton id='admin-section-sidebar' disabled={organizationLoadStatus !== LoadStatus.SUCCESS}>
                <ListItemText
                  sx={style.sidebarNavText}
                  primary={<Typography variant="drawer">{t('sidebar.manageOrganizations')}</Typography>}
                />
              </ListItemButton>
              <Button id='change-workspace-sidebar' size="small" variant="outlined" startIcon={<ChangeCircleOutlinedIcon />}>
                <Typography variant="drawerButtonOutlined">{t('sidebar.changeWorkspace')}</Typography>
              </Button>
            </Stack>
          </List>
          <Stack direction="column" justifyContent="flex-end" mb={4} sx={{ height: '100%' }}>
            <Stack spacing={2} mb="40px">
              {/* My account button hidden until it'simplementation finishes. */}
              <Button
                sx={{ display: 'none' }}
                size="large"
                id="organization-selection-sidebar"
                startIcon={<PersonIcon sx={style.buttonIcon} />}
              >
                <Typography variant="drawerButton">{t('sidebar.myAccount')}</Typography>
              </Button>
              <Button
                size="large"
                onClick={handleLogout}
                id="logout"
                className="logout-button"
                endIcon={<LogoutIcon sx={style.buttonIcon} />}
              >
                <Typography variant="drawerButton">{t('sidebar.logout')}</Typography>
              </Button>
            </Stack>

            <Stack variant="poweredSection">
              <Typography sx={{ fontSize: '12px', color: colors.white }}>{t('sidebar.poweredBy')}</Typography>
              <img height="22px" width="125px" src={logo} alt="Prosecom logo" />
            </Stack>
          </Stack>
        </>
      )}
    </MuiDrawer>
  );
}
